import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useScales } from "../hooks/use-scales";
import { ManualGpaCalculator } from "./manual-conversion";
import { FileUploader } from "./uploader";
import {
  AlertCircle,
  Bot,
  Check,
  ChevronsUpDown,
  Info,
  Loader2,
  Pen,
  FileUp,
  X,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  useCountries,
  useGradingScales,
} from "@/features/dashboard/queries/conversions.get.queries";
import { Skeleton } from "@/components/ui/skeleton";
import GpaScaleData, { EmptyGpaScaleData } from "./gpa-scales";
import { GpaScale } from "@/types/dashboard.types";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { PDFDocument } from "pdf-lib";
import { ScaleInfoDialog } from "./scale-info-dialog";
import { useAuth } from "@/providers/auth.provider";
import { useCustomGradingScales } from "@/features/dashboard/queries/custom-grading-scales.queries";
import { CreateCustomScaleModal } from "./create-custom-scale-modal";

function PDFViewer({ file }: { file: File | null }) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPdfUrl(null);
    }
  }, [file]);

  if (!file || !pdfUrl) {
    return (
      <div className="w-full h-[400px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <FileUp className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No PDF selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[400px] border rounded-lg overflow-hidden">
      <iframe src={pdfUrl} className="w-full h-full" title="PDF Viewer" />
    </div>
  );
}

function PDFInput({
  onFileSelect,
  selectedFile,
}: {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
}) {
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        try {
          if (file.type === "application/pdf") {
            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            if (pdfDoc.getPageCount() > 20) {
              toast.error("PDF should not exceed 20 pages.");
              return;
            }
          }
          onFileSelect(file);
          toast.success("PDF loaded successfully");
        } catch (error) {
          toast.error("Invalid PDF file");
        }
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 10MB
    multiple: false,
  });

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"}
          ${selectedFile ? "opacity-50" : ""}`}
      >
        <input {...getInputProps()} />
        <FileUp className="h-6 w-6 text-gray-400 mx-auto mb-2" />
        {isDragActive ? (
          <p className="text-sm text-gray-600">Drop the PDF here...</p>
        ) : (
          <div>
            <p className="text-sm text-gray-600">
              Drag & drop a PDF here, or click to select
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PDF files up to 10MB, max 20 pages -{" "}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ConvertTranscript() {
  const [isManual, setIsManual] = useQueryState(
    "isManual",
    parseAsBoolean.withDefault(false)
  );
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const { user } = useAuth();
  const [isCustomScalesMode, setIsCustomScalesMode] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data: countries, isPending: isCountriesFetching } = useCountries();
  const { setCountryId, setGradingScaleId, countryId, gradingScaleId } =
    useScales();
  const { data: fetchedGradingScales, isPending: isGradingScalesFetching } =
    useGradingScales(countryId);
  const { data: customGradingScales, isPending: isCustomScalesFetching } =
    useCustomGradingScales(user?.user?.id);

  const [countryPopoverOpen, setCountryPopoverOpen] = useState(false);
  const [gradingScaleOpen, setGradingScaleOpen] = useState(false);

  const scale = useMemo(() => {
    if (isCustomScalesMode && customGradingScales && gradingScaleId) {
      const currentScale = customGradingScales.find(
        (s) => String(s.id) === gradingScaleId
      );
      return currentScale ? currentScale.scale : null;
    } else if (fetchedGradingScales && gradingScaleId) {
      const currentScale = fetchedGradingScales.find(
        (s) => String(s.id) === gradingScaleId
      );
      return currentScale ? currentScale.scale : null;
    }
    return null;
  }, [
    fetchedGradingScales,
    customGradingScales,
    gradingScaleId,
    isCustomScalesMode,
  ]);

  const countryName = useMemo(() => {
    if (isCustomScalesMode) {
      return "My Custom Scales";
    }
    if (countries && countryId) {
      return countries.find((country) => String(country.id) === countryId)
        ?.name;
    }
    return null;
  }, [countries, countryId, isCustomScalesMode]);

  // Clear PDF file when switching modes
  const handleModeChange = (value: string) => {
    setIsManual(value === "manual");
    if (value !== "manual") {
      setPdfFile(null);
    }
  };

  const handleRemoveFile = () => {
    setPdfFile(null);
  };

  const handleCountrySelect = (countryId: string | null) => {
    if (countryId === "custom") {
      setIsCustomScalesMode(true);
      setCountryId(null);
      setGradingScaleId(null);
    } else {
      setIsCustomScalesMode(false);
      setCountryId(countryId);
      setGradingScaleId(null);
    }
    setCountryPopoverOpen(false);
  };

  const handleCustomScaleSelect = (scaleId: string) => {
    setGradingScaleId(scaleId);
    setGradingScaleOpen(false);
  };

  const handleCreateNewScale = () => {
    setIsCreateModalOpen(true);
    setGradingScaleOpen(false);
  };

  return (
    <div className="flex flex-col gap-4 px-3 py-8 overflow-hidden">
      <div className="flex gap-4">
        <Popover open={countryPopoverOpen} onOpenChange={setCountryPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              role="combobox"
              id="select-country"
              aria-expanded={countryPopoverOpen}
              variant={"outline"}
              className="w-1/3 justify-between"
            >
              {countryName ?? "Select Country"}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 max-h-96">
            <Command>
              <CommandInput placeholder="Search country..." />
              <CommandList className="max-h-64 overflow-y-auto">
                <CommandEmpty>
                  {isCountriesFetching ? (
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  ) : (
                    "No results found."
                  )}
                </CommandEmpty>

                <CommandGroup>
                  <CommandItem
                    value="custom"
                    onSelect={() => handleCountrySelect("custom")}
                    className="border-b border-gray-200 pb-2 mb-2"
                    id="custom-scales-option"
                  >
                    🎨 My Custom Scales
                  </CommandItem>
                </CommandGroup>

                <CommandGroup>
                  {countries?.map((currentValue) => (
                    <CommandItem
                      key={currentValue.id}
                      value={currentValue.name}
                      onSelect={() =>
                        handleCountrySelect(String(currentValue.id))
                      }
                    >
                      {currentValue.name}
                      <Check
                        className={cn(
                          "ml-auto",
                          String(currentValue.id) === countryId
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {isCustomScalesMode ? (
          <Popover open={gradingScaleOpen} onOpenChange={setGradingScaleOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                id="select-custom-grading-scale"
                aria-expanded={gradingScaleOpen}
                className="w-1/2 justify-between truncate overflow-hidden whitespace-nowrap"
                disabled={isCustomScalesFetching}
              >
                {gradingScaleId
                  ? customGradingScales?.find(
                      (gradingScale) =>
                        String(gradingScale.id) === gradingScaleId
                    )?.name
                  : "Select Custom Grading Scale..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 max-h-96">
              <Command>
                <CommandInput placeholder="Search custom grading scale..." />
                <CommandList className="max-h-64 overflow-y-auto">
                  <CommandEmpty>
                    {isCustomScalesFetching ? (
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-muted-foreground">
                          You don't have custom scales yet.
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Create one to get started!
                        </p>
                      </div>
                    )}
                  </CommandEmpty>

                  {/* Existing Custom Scales */}
                  {customGradingScales && customGradingScales.length > 0 && (
                    <CommandGroup>
                      {customGradingScales.map((currentValue, idx) => (
                        <CommandItem
                          key={idx}
                          value={currentValue.name + idx}
                          onSelect={() =>
                            handleCustomScaleSelect(String(currentValue.id))
                          }
                          className="cursor-pointer"
                        >
                          {currentValue.name}
                          <Check
                            className={cn(
                              "ml-auto",
                              String(currentValue.id) === gradingScaleId
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>

              {/* Create New Scale Option - OUTSIDE Command component, ALWAYS VISIBLE */}
              <div className="border-t border-gray-200 pt-2 mt-2">
                <button
                  onClick={handleCreateNewScale}
                  className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded-sm cursor-pointer flex items-center gap-2"
                >
                  ➕ Create New Custom Scale
                </button>
              </div>
            </PopoverContent>
          </Popover>
        ) : isGradingScalesFetching && countryId ? (
          <Skeleton className="h-10 w-1/2" />
        ) : (
          <Popover open={gradingScaleOpen} onOpenChange={setGradingScaleOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                id="select-grading-scale"
                aria-expanded={gradingScaleOpen}
                className="w-1/2 justify-between truncate overflow-hidden whitespace-nowrap"
                disabled={!countryId || isGradingScalesFetching}
              >
                {gradingScaleId
                  ? fetchedGradingScales?.find(
                      (gradingScale) =>
                        String(gradingScale.id) === gradingScaleId
                    )?.name
                  : "Select Grading Scale..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="overflow-y-auto w-96 max-h-96">
              <Command>
                <CommandInput placeholder="Search grading scale..." />
                <CommandList>
                  <CommandEmpty>No grading scale found.</CommandEmpty>
                  <CommandGroup>
                    {fetchedGradingScales?.map((currentValue, idx) => (
                      <CommandItem
                        key={idx}
                        value={currentValue.name + idx}
                        onSelect={() => {
                          setGradingScaleId(String(currentValue.id));
                          setGradingScaleOpen(false);
                        }}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            String(currentValue.id) === gradingScaleId
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        {currentValue.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        <div className="flex w-full items-center space-x-2">
          <Select
            value={isManual ? "manual" : "ai"}
            onValueChange={handleModeChange}
          >
            <SelectTrigger id="select-mode" className="w-full">
              <div className="flex items-center gap-3">
                {isManual ? null : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Only convert single-page transcripts</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <p>{isManual ? "Manual" : "AI"}</p>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem className="font-bold" value="manual">
                <Pen strokeWidth="2" />
                Manual
              </SelectItem>
              <SelectItem className="font-bold" value="ai">
                <Bot strokeWidth="2" />
                AI
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {!isManual && (
        <Alert variant={"warning"}>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>AI Conversion Limitation</AlertTitle>
          <AlertDescription className="flex">
            AI conversion currently supports only single-page transcripts. For
            <strong className="border-b text-warning-text">
              multi-page
            </strong>{" "}
            or{" "}
            <strong className="border-b text-warning-text">
              multi-grade documents
            </strong>{" "}
            , please use Manual conversion.
          </AlertDescription>
        </Alert>
      )}
      {
        <div className="flex gap-4 justify-between items-start w-full mt-4 relative">
          <div className="w-full overflow-y-auto">
            {isManual ? (
              <ManualGpaCalculator
                countryId={countryId ?? ""}
                gradingScaleId={gradingScaleId ?? ""}
                pdfFile={pdfFile}
              />
            ) : (
              <FileUploader
                countryId={countryId ?? ""}
                gradingScaleId={gradingScaleId ?? ""}
              />
            )}
          </div>
          <div className="max-w-[35vw] w-full h-fit overflow-y-auto overflow-x-clip rounded-md sticky top-0">
            {isManual ? (
              <div className="space-y-1">
                {!pdfFile && (
                  <PDFInput onFileSelect={setPdfFile} selectedFile={pdfFile} />
                )}
                {pdfFile && (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center grow justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileUp className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          {pdfFile.name}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveFile}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                <PDFViewer file={pdfFile} />
                <ScaleInfoDialog scale={scale} />
              </div>
            ) : (
              <>
                {scale ? (
                  <GpaScaleData data={scale as GpaScale[]} />
                ) : (
                  <EmptyGpaScaleData />
                )}
              </>
            )}
          </div>
        </div>
      }

      {user?.user?.id && (
        <CreateCustomScaleModal
          userId={user.user.id}
          isOpen={isCreateModalOpen}
          onOpenChange={setIsCreateModalOpen}
        />
      )}
    </div>
  );
}
